import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { App } from '../App';
import { fetchPublicMap, type Map } from '../services/apiClient';
import type { Place } from '../types/Place';
import { LoadingSkeleton } from '../components/LoadingSkeleton';

/**
 * PublicMapPage - Read-only view of a public map
 *
 * SPRINT-5.2: Public map sharing frontend
 * - Loads map by public slug from URL
 * - Displays map in read-only mode (no edit controls)
 * - Shows map title in header
 * - Includes "View your own maps" link
 */
export function PublicMapPage() {
  const { slug } = useParams<{ slug: string }>();
  const [map, setMap] = useState<Map | null>(null);
  const [places, setPlaces] = useState<Place[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadPublicMap() {
      if (!slug) {
        setError('Invalid map link');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const data = await fetchPublicMap(slug);
        setMap(data.map);
        setPlaces(data.places);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to load map';
        // Extract the actual error message from API response format
        const match = message.match(/API Error (\d+): (.+)/);
        if (match) {
          const statusCode = parseInt(match[1], 10);
          if (statusCode === 404) {
            setError('This map could not be found. It may have been removed or the link is incorrect.');
          } else {
            setError(match[2]);
          }
        } else {
          setError(message);
        }
      } finally {
        setIsLoading(false);
      }
    }

    loadPublicMap();
  }, [slug]);

  // Loading state
  if (isLoading) {
    return <LoadingSkeleton variant="full" message="Loading shared map..." />;
  }

  // Error state
  if (error || !map) {
    return (
      <div
        className="min-h-screen bg-bg-base flex items-center justify-center p-4"
        role="alert"
        aria-live="assertive"
      >
        <div className="max-w-md w-full bg-bg-surface rounded-2xl p-8 text-center">
          <div className="text-4xl mb-4" aria-hidden="true">
            🗺️
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">
            Map Not Found
          </h1>
          <p className="text-gray-400 mb-6">
            {error || 'This map could not be loaded.'}
          </p>
          <div className="flex flex-col gap-3">
            <Link
              to="/login"
              className="px-6 py-3 bg-primary hover:bg-primary/80 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 text-center"
            >
              View Your Own Maps
            </Link>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-bg-elevated hover:bg-bg-elevated/80 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white/20"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render the map in read-only mode
  return (
    <div className="relative">
      {/* Main map view - no onEditPlace means no edit buttons */}
      <App
        initialPlaces={places}
        mapTitle={map.title}
        // Not passing onEditPlace - this hides edit buttons in PlacePopup
      />

      {/* Footer with "Powered by Love Map" and login link */}
      <div className="fixed bottom-0 left-0 right-0 z-[999] pointer-events-none">
        <div className="flex items-center justify-center pb-20 lg:pb-24">
          <div className="pointer-events-auto bg-bg-surface/90 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-3 text-sm border border-gray-800">
            <span className="text-gray-400">Powered by Love Map</span>
            <span className="text-gray-600">|</span>
            <Link
              to="/login"
              className="text-primary hover:text-primary/80 transition-colors"
            >
              Create your own
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
